import 'uplot/dist/uPlot.min.css';

import {isEqual} from 'lodash';
import React, {type FC, useEffect, useMemo, useRef} from 'react';
import type uPlot from 'uplot';
import UplotReact from 'uplot-react';

import {type IssueLine} from '../../../state/issues/types';
import {recordRumAction} from '../../../util/datadog/datadogRum';
import {type PlotFontSize} from '../../../util/plotHelpers/plotFontSize';
import {type PlotMargin} from '../../../util/plotHelpers/plotHelpers';
import {
  type Line,
  type LineType,
  type PlotType,
  type Timestep,
} from '../../../util/plotHelpers/types';
import {useRampFlagUPlotUser} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {useColorMode} from '../../../util/useColorMode';
import {useStablePanelId} from '../../Panel/PanelIDContext';
import {usePanelConfigContext} from '../../PanelRunsLinePlot/PanelConfigContext';
import {useUpdateIconPositions} from '../LinePlotPlot';
import {type DomainMaybe} from '../types';
import {type IssueLinesConfig} from './drawIssueLines';
import {type NonFiniteMarkerState} from './drawNonFiniteMarkers';
import {type AlignmentProfileState} from './getAlignedData';
import {getUPlotOptions} from './getUPlotOptions';
import {
  getuPlotLabelFontSizePx,
  getuPlotMarginOverride,
  getuPlotXAxisTickValuesSizePx,
} from './margins';
import {type PreparedUPlotData, prepareUPlotData} from './prepareUPlotData';
import {useReactVisUsageTracking} from './useReactVisUsageTracking';

const SUPPORTED_LINE_TYPES: Set<LineType> = new Set(['line', 'area']);

export function useShouldUseuPlot(
  lines: Line[],
  plotType: PlotType | undefined
) {
  const uplotEnabled = useRampFlagUPlotUser() === true;
  const areAllLinesuPlotSupported = useMemo(() => {
    return lines.every(
      line => line.type != null && SUPPORTED_LINE_TYPES.has(line.type)
    );
  }, [lines]);
  const isChartTypeuPlotSupported = plotType === 'line';
  const panelConfig = usePanelConfigContext();
  const forceReactVis = panelConfig.forceReactVis === true;
  const shouldUseuPlot =
    uplotEnabled &&
    areAllLinesuPlotSupported &&
    isChartTypeuPlotSupported &&
    !forceReactVis;
  useReactVisUsageTracking(shouldUseuPlot, {
    areAllLinesuPlotSupported,
    forceReactVis,
    isChartTypeuPlotSupported,
    plotType,
    uplotEnabled,
  });
  return shouldUseuPlot;
}

const emptyIssueLines: IssueLine[] = [];

type LinePlotuPlotProps = {
  lines: Line[];
  /** Shared by the main and highlight layers; never mutated by a chart. */
  preparedData?: PreparedUPlotData;
  width: number;
  height: number;
  xDomain: DomainMaybe;
  yDomain: DomainMaybe;
  fontSize: PlotFontSize;
  timestep: Timestep | undefined;
  /** Issue lines to render on the chart */
  issueLines?: IssueLine[];
  /** ID of the currently hovered issue (for highlighting) */
  hoveredIssue?: string | null;
  /** Whether the x-axis min is zoomed (if true, hide 'before' lines) */
  isZoomedXMin?: boolean;
  /** Whether the x-axis max is zoomed (if true, hide 'after' lines) */
  isZoomedXMax?: boolean;
  /** If true, this is the overlay layer that contains the highlighted lines */
  isOverlay?: boolean;
  /** If true, this is the base layer that draws the grid */
  isBaseLayer?: boolean;
  /**
   * Overlay layer only: the exact line objects that should be drawn bright.
   * Visibility is applied imperatively per series so the
   * options reference stays stable and hovering never recreates the chart.
   */
  highlightedLines?: ReadonlySet<Line>;
  dimFactor?: number;
  /** Use to override the default react-vis plot margin in the crosshair component */
  plotMarginOverride: PlotMargin | undefined;
  setPlotMarginOverride?: (plotMargin: PlotMargin) => void;
};

export const LinePlotuPlot: FC<LinePlotuPlotProps> = ({
  lines,
  preparedData,
  width,
  height,
  xDomain,
  yDomain,
  fontSize,
  timestep,
  issueLines = emptyIssueLines,
  hoveredIssue = null,
  isZoomedXMin = false,
  isZoomedXMax = false,
  isOverlay = false,
  isBaseLayer = false,
  highlightedLines,
  dimFactor,
  plotMarginOverride,
  setPlotMarginOverride,
}) => {
  const panelID = useStablePanelId();

  const {xAxisIsDatetime, xLogScale, yLogScale} = usePanelConfigContext();
  // update the issues line icon position to match the plot margin
  useUpdateIconPositions(xDomain, plotMarginOverride);

  const alignmentProfileRef = useRef<AlignmentProfileState>({
    panelId: panelID,
    lastAlignedYSlotCount: 0,
  });
  const seriesEntriesResult = useMemo(
    function prepareLayerData() {
      if (alignmentProfileRef.current.panelId !== panelID) {
        alignmentProfileRef.current = {
          panelId: panelID,
          lastAlignedYSlotCount: 0,
        };
      }
      return (
        preparedData ??
        prepareUPlotData(lines, xLogScale, alignmentProfileRef.current)
      );
    },
    [preparedData, lines, xLogScale, panelID]
  );
  const {data} = seriesEntriesResult;
  const uplotRef = useRef<uPlot | null>(null);

  // State ref for non-finite markers - mutable state that draw/setCursor hooks can access
  const nonFiniteMarkerStateRef = useRef<NonFiniteMarkerState>({
    hoveredMarkerIndex: -1,
    dimFactor,
  });
  nonFiniteMarkerStateRef.current.dimFactor = dimFactor;

  const issueLinesConfig: IssueLinesConfig = useMemo(
    () => ({
      issueLines,
      hoveredIssue,
      isZoomedXMin,
      isZoomedXMax,
    }),
    [issueLines, hoveredIssue, isZoomedXMin, isZoomedXMax]
  );

  const {isDarkMode} = useColorMode();

  /**
   * Options are memoized and should avoid updating unless you want to re-render the entire plot.
   * If you need to access some state that changes in a uplot hook (like the nonFiniteMarkerState),
   * it's better to do it via a ref that can be accessed in the hook. This way the options itself
   * don't change and re-render the entire plot.
   */
  const options = useMemo(
    () =>
      getUPlotOptions(
        seriesEntriesResult.seriesEntries,
        seriesEntriesResult.lineNameMap,
        lines,
        nonFiniteMarkerStateRef,
        issueLinesConfig,
        width,
        height,
        xDomain,
        yDomain,
        xAxisIsDatetime,
        xLogScale,
        yLogScale,
        fontSize,
        timestep,
        isDarkMode,
        isOverlay,
        isBaseLayer
      ),
    [
      seriesEntriesResult,
      lines,
      issueLinesConfig,
      width,
      height,
      xDomain,
      yDomain,
      xAxisIsDatetime,
      xLogScale,
      yLogScale,
      fontSize,
      timestep,
      isDarkMode,
      isOverlay,
      isBaseLayer,
    ]
  );
  const lastBBoxRef = useRef<uPlot.BBox | null>(null);

  // has to be in useEffect to avoid bad setState call
  useEffect(() => {
    // Only use bbox values when uPlot is fully ready (status === 1)
    // status 0 means the chart is still initializing and bbox values are incorrect
    // This way we can avoid overtriggering updating the plot margin override.
    if (
      setPlotMarginOverride &&
      uplotRef.current != null &&
      uplotRef.current.status === 1 &&
      !isEqual(uplotRef.current.bbox, lastBBoxRef.current)
    ) {
      lastBBoxRef.current = uplotRef.current.bbox;

      // This aligns the crosshair overlay on top of the uPlot chart no matter what the
      // margins are automatically set by uPlot.
      const margin = getuPlotMarginOverride(
        uplotRef.current.width,
        uplotRef.current.height,
        lastBBoxRef.current
      );
      if (margin) {
        setPlotMarginOverride(margin);
      }
    }
  }, [
    setPlotMarginOverride,
    uplotRef.current?.bbox,
    uplotRef.current?.width,
    uplotRef.current?.height,
    lastBBoxRef.current?.width,
    lastBBoxRef.current?.height,
    uplotRef.current?.status,
  ]);

  // Highlighting is applied imperatively as per-series visibility rather than by
  // changing the `lines`/`options` props. A stable options reference stops
  // uplot-react from treating the change as 'create', which previously forced a
  // destroy()+new uPlot() — and its synchronous "Recalculate Style" — on every
  // hover. We touch only the series whose visibility actually changed, so a
  // steady hover over one run does no work here after the first frame.
  useEffect(() => {
    const u = uplotRef.current;
    if (u == null || highlightedLines == null) {
      return;
    }
    const {seriesEntries} = seriesEntriesResult;
    for (let i = 1; i < seriesEntries.length; i++) {
      const entry = seriesEntries[i];
      if (entry == null) {
        continue;
      }
      const shouldShow = highlightedLines.has(entry.line);
      if (u.series[i]?.show !== shouldShow) {
        u.setSeries(i, {show: shouldShow});
      }
    }
  }, [highlightedLines, seriesEntriesResult]);

  const smoothedFromBackend = lines.some(
    l => l.smoothedLineFromBackend != null
  );

  const hasSmoothedLines = smoothedFromBackend || lines.some(l => l.smoothed);

  useEffect(() => {
    if (hasSmoothedLines) {
      recordRumAction('line_plot_smoothed', {
        panelID,
        smoothedFromBackend,
        uplot: true,
      });
    }
  }, [panelID, hasSmoothedLines, smoothedFromBackend]);

  return (
    <div
      className="w-full h-full relative"
      data-test="line-plot-uplot"
      data-smoothed-from-backend={smoothedFromBackend}>
      <UplotReact
        options={options}
        data={data}
        onCreate={u => {
          uplotRef.current = u;
        }}
      />
      {/* Axis titles are rendered by the parent (LinePlot) in reserved strips
          outside the plot, so they no longer overlap the chart. */}
    </div>
  );
};
